import React, {useEffect, useState} from 'react';
import moment from "moment/moment";
import {getGcsLink} from "./loggingDagContentColumns";
import AirflowDagLogs from "../Components/airflow/dagLogs/AirflowDagLogs";
import CheckpointDropdown from "../Components/CheckpointDropdown";
import {RadioButtonGroup} from "../../utils/components/RadioButtonGroup";
import StoreInsightsTableCheckboxList from "./StoreInsightsTableCheckboxList";
import SelectDropdown from "../Components/Dropdowns/SelectDropdown";
import PeriodsDropdown from "../Components/Dropdowns/PeriodsDropdown";
import Checkbox from "../Components/Checkbox";
import {NumberSelectionDropdown} from "../Components/Dropdowns/NumberSelectionDropdown";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import {toastError, toastSuccess} from "../../utils/toast";
import {toast} from "react-toastify";
import {invokeDAGviaApiPost} from "../../Api/invokeDag";
import {getUserEmail} from "../../auth/accessTokenValidator";
import {makeApolloClientCall} from "../../Api/makeApolloApiCall";
import {makeApiCall} from "../../Api/makeApiCall";
import Page from "../Components/Page";
import {Breadcrumbs} from "../Components/Breadcrumbs";
import {gql} from "@apollo/client";
import {todayChicago} from "../Alerts/MissingData/filters";

// noinspection GraphQLUnresolvedReference
export const currentWeekIdQuery = gql`query {
    currentPeriodWeek: periods (isCurrentPeriodWeek: true) {
        weekId
        quadId
        endDate
        periodAbbreviation
        spinsWeek
        isCurrentPeriod
    },
    currentPeriodQuad: periods (isCurrentPeriodQuad: true) {
        weekId
        quadId
        endDate
        periodAbbreviation
        spinsWeek
        isCurrentPeriod
    }
}`;

// noinspection HtmlUnknownTarget
export const SLD_RELEASE_OPTION = {
    SKIP_STORE_CONFIG: (<span>Skip Store Configuration <a href="/configurations/store/skip" target="_blank"
                                                          rel="noopener noreferrer">(link)</a></span>),
    SALESFORCE_OPT_OUT: 'SalesForce Store Level Release Opt Out'
}

const SldReleasePage = () => {

    const DAG_NAME = 'publish_store_insights'

    const [selectedWeekId, setSelectedWeekId] = useState(undefined);
    const [selectedWeekIdLabel, setSelectedWeekIdLabel] = useState(undefined);
    const [selectedNumberOfWeeks, setSelectedNumberOfWeeks] = useState(110);
    const [checkpointPath, setCheckpointPath] = useState(undefined);
    const [snapshotOptions, setSnapshotOptions] = useState(undefined);
    const [selectedSnapshot, setSelectedSnapshot] = useState(undefined);
    const [storesVersionOptions, setStoresVersionOptions] = useState(undefined);
    const [projectionOptions, setProjectionOptions] = useState(undefined);
    const [selectedStoresVersion, setSelectedStoresVersion] = useState(undefined);
    const [typeSelection, setTypeSelection] = useState('Weekly');
    const [selectedInsightStart, setSelectedInsightStart] = useState(undefined);
    const [selectedMarketPeriod, setSelectedMarketPeriod] = useState(undefined);
    const [storeLevelReleaseOptOutChecked, setStoreLevelReleaseOptOutChecked] = useState(false);
    const [currentPeriodWeek, setCurrentPeriodWeek] = useState(undefined);
    const [currentPeriodQuad, setCurrentPeriodQuad] = useState(undefined);
    const [selectedMarketProjectionVersion, setSelectedMarketProjectionVersion] = useState('current');
    const [includeDiscontinuedMarketStores, setIncludeDiscontinuedMarketStores] = useState(true);
    const [datasetsToGenerate, setDatasetsToGenerate] = useState([]);
    const [periodWeekOptions, setPeriodWeekOptions] = useState([]);
    const [insightEndOptions, setInsightEndOptions] = useState([]);
    const [marketPeriodOptions, setMarketPeriodOptions] = useState([]);

    const [loading, setLoading] = useState(true);


    function getCurrentTimestamp() {
        const format = "YYYYMMDDHHmmss";
        const currentDate =  todayChicago();
        return moment(currentDate).utc().format(format);
    }

    useEffect(() => {
        setLoading(true);
        Promise.all([
            makeApolloClientCall(currentWeekIdQuery, true),
            makeApolloClientCall(`{ periods { weekId, isCurrentPeriodQuad } }`, true),
            makeApolloClientCall(`{ periods(periodEndWeeksOnly: true) { weekId, periodAbbreviation, isCurrentPeriodQuad } }`, true),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/productSnapshotVersions`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/timeService`, {insightsPeriod: true}),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/storesVersions`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/projectionsVersions`),
            makeApolloClientCall('{ marketPeriodList }')
        ])
            .then(([
                       currentWeekIdQueryResults,
                       periodWeekResults,
                       periodInsightEndResults,
                       productSnapshotVersionsResults,
                       timeServiceResults,
                       storesVersionsResults,
                       projectionsVersionsResults,
                       marketPeriodResults
                   ]) => {
                const currentPeriodWeek = currentWeekIdQueryResults.currentPeriodWeek?.[0]?.weekId;
                const currentPeriodQuad = currentWeekIdQueryResults.currentPeriodQuad?.[0]?.weekId;
                const currentPeriodAbbreviation = currentWeekIdQueryResults.currentPeriodQuad?.[0]?.periodAbbreviation;
                setSelectedWeekId(currentPeriodWeek);
                setCurrentPeriodWeek(currentPeriodWeek);
                setCurrentPeriodQuad(currentPeriodQuad);
                setSelectedMarketPeriod(currentPeriodAbbreviation);

                const periodWeekResultsMapped = periodWeekResults.periods.map(period => {
                    const labelWeekId = period.isCurrentPeriodQuad ? `${period.weekId} (Current Period)` : period.weekId.toString();
                    return {label: labelWeekId, value: period.weekId};
                });
                setPeriodWeekOptions(periodWeekResultsMapped);


                const periodInsightEndResultsMapped = periodInsightEndResults.periods.map(period => {
                    const labelWeekId = period.isCurrentPeriodQuad ? `${period.periodAbbreviation} (Current Period Quad)` : period.periodAbbreviation.toString();
                    return {label: labelWeekId, value: period.weekId};
                });
                setInsightEndOptions(periodInsightEndResultsMapped);

                const snapshotOptions = productSnapshotVersionsResults.sort().reverse().map(option => ({
                    label: option,
                    value: option
                }));
                setSnapshotOptions(snapshotOptions);
                setSelectedSnapshot(snapshotOptions[0]);
                setSelectedInsightStart(timeServiceResults.periodAbbreviation);

                const storesVersionOptions = storesVersionsResults.sort().reverse().map(sv => ({label: sv, value: sv}));
                setStoresVersionOptions(storesVersionOptions);
                setSelectedStoresVersion(storesVersionOptions[0]);

                const projectionsVersionsResultsOptions = projectionsVersionsResults.map(pv => ({label: pv, value: pv}));
                setProjectionOptions(projectionsVersionsResultsOptions);

                const marketPeriodOptions = marketPeriodResults.marketPeriodList.sort().reverse().map(mp => ({label: mp, value: mp}));
                setMarketPeriodOptions(marketPeriodOptions);
                setLoading(false);
            })
            .finally(() => setLoading(false));
    }, [])

    const isPeriodType = typeSelection === 'Period';
    const isWeeklyType = typeSelection !== 'Period';

    const triggerDag = () => {
        const data = {
            weekId: selectedWeekId,
            triggered_by: getUserEmail(),
            checkpoint_path: checkpointPath,
            snapshotVersion: selectedSnapshot?.value,
            storesVersion: selectedStoresVersion?.value,
            datasets_to_generate: datasetsToGenerate,
            is_store_level_release_opt_out: storeLevelReleaseOptOutChecked,
            ...(typeSelection === 'Weekly' && {selectedNumberOfWeeks: selectedNumberOfWeeks}),
            ...(isPeriodType && {
                selected_insight_start: selectedInsightStart,
                ...(!includeDiscontinuedMarketStores && {stores_in_markets_lookup_period: selectedMarketPeriod}),
                stores_in_markets_lookup_projection_version: selectedMarketProjectionVersion,
                include_discontinued_market_stores: includeDiscontinuedMarketStores
            }),
            selected_type: typeSelection,
            timestamp: getCurrentTimestamp()
        }
        invokeDAGviaApiPost(DAG_NAME, data)
            .then((response) => {
                if (response?.apiError)
                    toastError(toast, `${DAG_NAME} DAG trigger failed with error ${JSON.stringify(response.apiError)}`)
                else
                    toastSuccess(toast, `${DAG_NAME} DAG triggered successfully with response ${JSON.stringify(response)}`)
            })
            .catch(e => {
                toastError(toast, `${DAG_NAME} DAG trigger failed with error ${JSON.stringify(e)}`)
            })
    };

    const getDefaultCurrentOption = value => {
        const currentWeekOrQuad = value === 'Weekly' ? currentPeriodWeek : currentPeriodQuad;
        const options = value === 'Weekly' ? periodWeekOptions : insightEndOptions;
        const current = options.find(option => option.value === currentWeekOrQuad);
        setSelectedWeekId(current.value);
        setSelectedWeekIdLabel(current.label);
    };

    return (
        <Page name="Store Insights Release"
              breadCrumbs={<Breadcrumbs items={[{label: "Store Insights"}, {label: "Generate"}]}/>}>
            <LoadingWrapper loading={loading}>
                <RadioButtonGroup
                    callback={(value) => {
                        setTypeSelection(value === 'Weekly' ? 'Weekly' : 'Period');
                        getDefaultCurrentOption(value);
                    }}
                    selectedValue={typeSelection === 'Weekly' ? 'Weekly' : 'Period'}
                    radioButtonOptions={['Weekly', 'Period']}
                />
                {isWeeklyType && <SelectDropdown id={"weekly-type-period_dropdown"}
                                                 dataTestId={"weekly-type-period_dropdown"}
                                                 inputLabel={"Period Week"}
                                                 selectValue={selectedWeekId}
                                                 selectLabel={selectedWeekIdLabel}
                                                 options={periodWeekOptions}
                                                 onChange={(option) => {
                                                     setSelectedWeekId(option.value);
                                                     setSelectedWeekIdLabel(option.label);
                                                 }}
                />}
                {isPeriodType && <SelectDropdown id={"period-type-period_dropdown"}
                                                 dataTestId={"period-type-period_dropdown"}
                                                 inputLabel={"Insight End"}
                                                 selectValue={selectedWeekId}
                                                 selectLabel={selectedWeekIdLabel}
                                                 options={insightEndOptions}
                                                 onChange={(option) => {
                                                     setSelectedWeekId(option.value);
                                                     setSelectedWeekIdLabel(option.label);
                                                 }}
                />}
                <br/>
                <br/>
                {isWeeklyType &&
                    <NumberSelectionDropdown
                        dataTestId={"weekly-number-of-weeks-dropdown"}
                        labelText="Number of Weeks" minNumber={1} maxNumber={150}
                        selectedValue={selectedNumberOfWeeks}
                        onChange={(v) => setSelectedNumberOfWeeks(v.value)}
                    />}
                {isPeriodType &&
                    <PeriodsDropdown
                        id={"insights_start"}
                        updatePeriod={(period) => setSelectedInsightStart(period)}
                        selectedPeriod={selectedInsightStart}
                        periodLabel={'Insight Start'}
                    />}
                <br/>
                <br/>
                <SelectDropdown
                    id={"sld-dropdown-product-snapshot_dropdown"}
                    dataTestId={"sld-dropdown-product-snapshot_dropdown"}
                    inputLabel={"Product snapshot"}
                    selectValue={selectedSnapshot?.value}
                    selectLabel={selectedSnapshot?.label}
                    placeholder={"Select Product Snapshot Version..."}
                    options={snapshotOptions}
                    onChange={(option) => setSelectedSnapshot(option ? option : undefined)}
                />
                {isPeriodType &&
                    <>
                        <br/>
                        <br/>
                        <Checkbox labelText={'Include Discontinued Market Stores'}
                                  onChange={(value) => setIncludeDiscontinuedMarketStores(value)}
                                  checked={includeDiscontinuedMarketStores}
                        />
                        <SelectDropdown id={"market_period_dropdown"}
                                        dataTestId={"market_period_dropdown"}
                                        inputLabel={"Market Week"}
                                        selectValue={selectedMarketPeriod}
                                        options={marketPeriodOptions}
                                        onChange={(option) => setSelectedMarketPeriod(option.value)}
                                        isDisabled={includeDiscontinuedMarketStores}
                        />
                        <br/>
                        <br/>
                        <SelectDropdown id={"market_store_projections_version_dropdown"}
                                        dataTestId={"market_store_projections_version_dropdown"}
                                        inputLabel={"Market Store (Projections) Version"}
                                        selectValue={selectedMarketProjectionVersion}
                                        options={projectionOptions}
                                        onChange={(option) => setSelectedMarketProjectionVersion(option.value)}
                        />
                    </>
                }
                <div>
                    <br/>
                    <SelectDropdown
                        id={"sld-dropdown-stores-version_dropdown"}
                        dataTestId={"sld-dropdown-stores-version_dropdown"}
                        inputLabel={"Stores Version"}
                        selectValue={selectedStoresVersion?.value}
                        selectLabel={selectedStoresVersion?.label}
                        placeholder={"Select Stores Version..."}
                        options={storesVersionOptions}
                        onChange={(option) => setSelectedStoresVersion(option ? option : undefined)}
                    />
                </div>
                <StoreInsightsTableCheckboxList
                    onCheckboxChange={(datasetsToGenerate) => setDatasetsToGenerate(datasetsToGenerate)}
                />
                <div>Do not include stores using:</div>
                <RadioButtonGroup
                    callback={(value) => setStoreLevelReleaseOptOutChecked(value !== SLD_RELEASE_OPTION.SKIP_STORE_CONFIG)}
                    selectedValue={!storeLevelReleaseOptOutChecked ? SLD_RELEASE_OPTION.SKIP_STORE_CONFIG : SLD_RELEASE_OPTION.SALESFORCE_OPT_OUT}
                    radioButtonOptions={Object.values(SLD_RELEASE_OPTION)}
                />
                <CheckpointDropdown
                    updateCheckpointPath={({checkpointPath}) => setCheckpointPath(checkpointPath)}
                    datasets={['cleanse_fill_extract', 'filtered_promo', 'promo_extract', 'store_insights']}
                    tableData={['promo_parquet']}
                />
                <div id="weeklyFactsActionMessage" data-testid={'weekly-facts-action-message'}
                     style={{marginTop: '15px'}}>
                    This will generate
                    the <strong>{selectedWeekId || '[week-id]'}_[timestamp]_weekly_facts</strong> table.
                </div>
                <br/>
                <button type="button"
                        className="sld-trigger-button button primary submit"
                        onClick={triggerDag}>
                    Generate Selected Store Insight Tables
                </button>
            </LoadingWrapper>
            <div className="sld-dag-airflowlogs-container" data-testid="airflow-logs-container">
                <h5>Store Insights Run Log</h5>
                <AirflowDagLogs
                    processName={'publish_store_insights'}
                    customColumns={[
                        {
                            header: 'Type',
                            accessorKey: 'conf.selected_type',
                            className: 'non-numeric-field'
                        },
                        {
                            header: 'Week Id',
                            accessorKey: 'weekid',
                            className: 'non-numeric-field'
                        },
                        {
                            header: 'Checkpoint Used',
                            accessorKey: 'checkpointused',
                            className: 'non-numeric-field',
                            enableColumnFilter: false,
                            cell: ({getValue}) => getValue() ? getGcsLink(getValue()) : <></>
                        }
                    ]}
                />
            </div>

        </Page>
    )
};

export default SldReleasePage;